import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createJob, JobMode, JobSummary } from "../api/jobs";
import { listPhones, Phone } from "../api/phones";
import { getScript, parseScriptInputs, ScriptInput, ScriptSummary } from "../api/scripts";

interface Props {
  script: ScriptSummary | { id: string; name: string; job_type: string };
  onClose: () => void;
  onLaunched: (job: JobSummary) => void;
}

export function LaunchJobModal({ script, onClose, onLaunched }: Props): JSX.Element {
  const [phones, setPhones] = useState<Phone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState(`${script.name} — ${new Date().toISOString().slice(0, 16).replace("T", " ")}`);
  const [phoneId, setPhoneId] = useState<string>("");
  const [mode, setMode] = useState<JobMode>("one_shot");
  const [maxRunsMode, setMaxRunsMode] = useState<"limited" | "infinite">("limited");
  const [maxRuns, setMaxRuns] = useState<number>(1);

  // Inputs déclarés par le script (popup "ask_user") + valeurs saisies.
  const [inputs, setInputs] = useState<ScriptInput[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      try {
        const [items, full] = await Promise.all([listPhones(), getScript(script.id)]);
        setPhones(items);
        if (items.length > 0) setPhoneId(items[0].id);
        const parsed = parseScriptInputs(full.content);
        setInputs(parsed);
        setInputValues(
          Object.fromEntries(
            parsed.map((i) => [i.var, i.default != null ? String(i.default) : ""])
          )
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [script.id]);

  async function handleSubmit() {
    if (!phoneId) {
      setError("Sélectionnez un téléphone");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Construit params depuis les inputs : type number → nombre, sinon string.
      const params: Record<string, unknown> = {};
      for (const inp of inputs) {
        const raw = inputValues[inp.var] ?? "";
        if (inp.type === "number") {
          const n = Number(raw);
          params[inp.var] = Number.isFinite(n) ? n : raw;
        } else {
          params[inp.var] = raw;
        }
      }
      const job = await createJob({
        name: name.trim() || `${script.name} (job)`,
        script_id: script.id,
        phone_id: phoneId,
        mode,
        max_runs: mode === "one_shot" ? 1 : maxRunsMode === "infinite" ? null : maxRuns,
        ...(inputs.length > 0 ? { params } : {}),
      });
      onLaunched(job);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de création");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-ink-800 rounded-xl border border-ink-700 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-ink-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Lancer un job</h2>
            <p className="text-xs text-ink-500 mt-0.5">Script : {script.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading ? (
            <div className="text-center text-ink-500 py-6">Chargement des téléphones…</div>
          ) : phones.length === 0 ? (
            <div className="text-center text-ink-500 py-6">
              Aucun téléphone enregistré. Créez-en un dans <strong>Téléphones</strong> d'abord.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1.5">
                  Nom du job
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1.5">
                  Téléphone
                </label>
                <select
                  value={phoneId}
                  onChange={(e) => setPhoneId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none"
                >
                  {phones.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.status})
                    </option>
                  ))}
                </select>
              </div>

              {inputs.length > 0 && (
                <div className="space-y-3 rounded-lg border border-ink-700 bg-ink-900/30 p-3">
                  <p className="text-xs uppercase tracking-wider text-velvet-300">
                    Paramètres du script
                  </p>
                  {inputs.map((inp) => (
                    <div key={inp.var}>
                      <label className="block text-xs text-ink-300 mb-1.5">{inp.prompt}</label>
                      <input
                        type={inp.type === "number" ? "number" : "text"}
                        value={inputValues[inp.var] ?? ""}
                        placeholder={inp.placeholder ?? ""}
                        onChange={(e) =>
                          setInputValues((prev) => ({ ...prev, [inp.var]: e.target.value }))
                        }
                        className="w-full px-3 py-2 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1.5">
                  Mode d'exécution
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMode("one_shot")}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                      mode === "one_shot"
                        ? "bg-velvet-600/20 border-velvet-500 text-velvet-200"
                        : "bg-ink-700/40 border-ink-600 text-ink-400 hover:text-white"
                    }`}
                  >
                    One-shot (1 run)
                  </button>
                  <button
                    onClick={() => setMode("loop")}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                      mode === "loop"
                        ? "bg-velvet-600/20 border-velvet-500 text-velvet-200"
                        : "bg-ink-700/40 border-ink-600 text-ink-400 hover:text-white"
                    }`}
                  >
                    Loop (multiples)
                  </button>
                </div>
              </div>

              {mode === "loop" && (
                <div>
                  <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1.5">
                    Limite de runs
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-ink-300">
                      <input
                        type="radio"
                        checked={maxRunsMode === "limited"}
                        onChange={() => setMaxRunsMode("limited")}
                        className="accent-velvet-500"
                      />
                      Limité à
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={maxRuns}
                      disabled={maxRunsMode !== "limited"}
                      onChange={(e) => setMaxRuns(parseInt(e.target.value, 10) || 1)}
                      className="w-24 px-3 py-1.5 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none disabled:opacity-50"
                    />
                    <span className="text-sm text-ink-500">runs</span>

                    <label className="flex items-center gap-2 text-sm text-ink-300 ml-4">
                      <input
                        type="radio"
                        checked={maxRunsMode === "infinite"}
                        onChange={() => setMaxRunsMode("infinite")}
                        className="accent-velvet-500"
                      />
                      Infini
                    </label>
                  </div>
                </div>
              )}

              {error && (
                <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-ink-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={submitting || loading || phones.length === 0}
            className="px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 disabled:bg-ink-700 disabled:text-ink-500 text-white text-sm font-medium transition-colors"
          >
            {submitting ? "Lancement…" : "Lancer"}
          </button>
        </div>
      </div>
    </div>
  );
}
